import { useState } from 'react';
import type { EditorMember } from './builder/Editor.tsx';
import { Sprite } from './Sprite.tsx';
import type { SpeciesId } from '../types.ts';

// Chat composer at the bottom of the conversation. Supports text input +
// .txt attachment for Showdown export drops. Submit on Enter (Shift+Enter
// for newline). Disabled state shown when the agent is busy.
//
// `onOpenBuilder` (optional) wires up the "build" pill — chat surface uses
// this to pop the builder modal without leaving the conversation.
//
// `attachedTeam` shows a chip with sprites when the user has built a team
// in the modal and clicked "attach to chat". Clicking the chip re-opens
// the modal pre-loaded; the × clears the attachment. The team is sent to
// the agent (prepended as a Showdown export) on submit — handled by the
// caller via onSubmit.
export function Composer({
  disabled = false,
  onSubmit,
  onOpenBuilder,
  attachedTeam,
  onClearAttachment,
  onClickAttachment,
}: {
  disabled?: boolean;
  onSubmit?: (text: string, attachments: File[]) => void;
  onOpenBuilder?: () => void;
  attachedTeam?: Array<EditorMember | null> | null;
  onClearAttachment?: () => void;
  onClickAttachment?: () => void;
}) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  function submit() {
    if (!text.trim() && files.length === 0) return;
    onSubmit?.(text, files);
    setText('');
    setFiles([]);
  }

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        background: 'var(--bg-0)',
        borderTop: '1px solid var(--line-1)',
        padding: '10px 14px',
      }}
    >
      {attachedTeam && attachedTeam.some((m) => m != null) && (
        <div style={{ marginBottom: 8 }}>
          <button
            type="button"
            onClick={onClickAttachment}
            title="Click to edit this team — it'll be attached to your next message"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 8px 4px 6px',
              background: 'var(--accent-bg)',
              border: '1px solid var(--accent-line)',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            <span
              className="caps mono"
              style={{ color: 'var(--accent-2)', fontSize: 9.5, letterSpacing: '0.08em' }}
            >
              team attached
            </span>
            <span style={{ display: 'inline-flex', gap: 2 }}>
              {attachedTeam
                .filter((m): m is EditorMember => m != null)
                .slice(0, 6)
                .map((m, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: slot index is the identity
                  <Sprite key={i} species={(m.species ?? '') as SpeciesId} size={20} />
                ))}
            </span>
            <span
              role="button"
              aria-label="remove team attachment"
              onClick={(e) => {
                e.stopPropagation();
                onClearAttachment?.();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  onClearAttachment?.();
                }
              }}
              tabIndex={0}
              style={{
                color: 'var(--fg-3)',
                fontSize: 14,
                padding: '0 4px',
                cursor: 'pointer',
              }}
            >
              ×
            </span>
          </button>
        </div>
      )}
      {files.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {files.map((f, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: filename can repeat across drops
              key={`${f.name}-${i}`}
              className="mono"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 8px',
                background: 'var(--bg-2)',
                border: '1px solid var(--line-1)',
                borderRadius: 3,
                fontSize: 11,
                color: 'var(--fg-2)',
              }}
            >
              {f.name}
              <button
                type="button"
                onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}
                style={{ color: 'var(--fg-4)' }}
                aria-label="remove attachment"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          padding: '8px 10px',
          background: 'var(--bg-1)',
          border: '1px solid var(--line-1)',
          borderRadius: 6,
        }}
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={disabled ? 'agent is thinking…' : 'ask vgcbuddy · drop a Showdown export'}
          disabled={disabled}
          aria-label="message vgcbuddy"
          rows={1}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--fg-1)',
            fontFamily: 'var(--font-sans)',
            fontSize: 13.5,
            resize: 'none',
            lineHeight: 1.5,
            minHeight: 22,
            maxHeight: 200,
          }}
        />
        <FileButton onFiles={(f) => setFiles((prev) => [...prev, ...f])} />
        {onOpenBuilder && (
          <button
            type="button"
            onClick={onOpenBuilder}
            className="caps mono"
            title="Open the team builder"
            aria-label="open team builder"
            style={{
              padding: '4px 8px',
              background: 'transparent',
              border: '1px solid var(--line-1)',
              borderRadius: 3,
              color: 'var(--fg-3)',
              fontSize: 10,
              letterSpacing: '0.08em',
              cursor: 'pointer',
            }}
          >
            build
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={disabled || (!text.trim() && files.length === 0)}
          className="caps mono"
          style={{
            padding: '4px 10px',
            background: disabled ? 'var(--bg-2)' : 'var(--accent-bg)',
            border: '1px solid var(--accent-line)',
            borderRadius: 3,
            color: disabled ? 'var(--fg-4)' : 'var(--accent-2)',
            fontSize: 10,
            letterSpacing: '0.08em',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          send
        </button>
      </div>
    </div>
  );
}

function FileButton({ onFiles }: { onFiles: (files: File[]) => void }) {
  return (
    <label
      style={{
        cursor: 'pointer',
        color: 'var(--fg-3)',
        padding: '4px 6px',
        fontSize: 12,
        border: '1px solid var(--line-1)',
        borderRadius: 3,
      }}
      title="attach Showdown export"
    >
      📎
      <input
        type="file"
        accept=".txt,text/plain"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          if (!e.target.files) return;
          onFiles(Array.from(e.target.files));
          e.target.value = '';
        }}
      />
    </label>
  );
}
